import type { BlockField } from '@/blocks/domain/types';
import type { HeaderContent, HeaderSection, SectionLayout } from '@/blocks/domain/types/BlockContent/HeaderContent';
import { canonicalizeHeader, isImplicitSection, normalizeHeaderSections, sectionColumns } from './headerSections';

/**
 * The Section > Column > element TREE of a header block and every edit the Sections panel can make
 * to it (LLAMS-610 DB-12). Pure and React-free: the panel's drag-and-drop, its up/down buttons and
 * the Add panel all funnel through here, and every result goes through `canonicalizeHeader`, so the
 * panel, the canvas and the persisted content cannot disagree about order.
 */

export interface TreeSection {
  id: string;
  layout: SectionLayout;
  /** A lone single-column element — the panel shows it as a plain row, not a "Section N". */
  implicit: boolean;
  /** 1-based number among the visible (non-implicit) sections; null for an implicit one. */
  sectionNumber: number | null;
  columns: BlockField[][];
}

export type TreeTarget =
  | { kind: 'column'; sectionId: string; column: number; index: number }
  /** A loose (implicit) section at this top-level position — an index in the FINAL section list. */
  | { kind: 'top'; index: number };

export function sectionTreeOf(content: HeaderContent): TreeSection[] {
  const normalized = normalizeHeaderSections(content);
  let visible = 0;
  return normalized.sections.map((section) => {
    const count = normalized.fields.filter((f) => f.sectionId === section.id).length;
    const implicit = isImplicitSection(section, count);
    if (!implicit) visible += 1;
    return {
      id: section.id,
      layout: section.layout,
      implicit,
      sectionNumber: implicit ? null : visible,
      columns: sectionColumns(section, normalized.fields)
    };
  });
}

function rebuild(content: HeaderContent, sections: HeaderSection[], fields: BlockField[]): HeaderContent {
  const canon = canonicalizeHeader(sections, fields);
  return { ...content, columns: undefined, sections: canon.sections, fields: canon.fields };
}

/** Auto-minted loose sections (`sec-of-<fieldId>`) vanish once emptied; authored ones stay. */
function pruneEmptyLoose(sections: HeaderSection[], fields: BlockField[]): HeaderSection[] {
  const used = new Set(fields.map((f) => f.sectionId));
  return sections.filter((s) => used.has(s.id) || !(s.layout === '1' && s.id.startsWith('sec-of-')));
}

function insertAt<T>(list: T[], index: number, item: T): T[] {
  const at = Math.max(0, Math.min(index, list.length));
  return [...list.slice(0, at), item, ...list.slice(at)];
}

export function moveElement(content: HeaderContent, fieldId: string, target: TreeTarget): HeaderContent {
  const normalized = normalizeHeaderSections(content);
  const moving = normalized.fields.find((f) => f.id === fieldId);
  if (!moving) return content;
  const rest = normalized.fields.filter((f) => f.id !== fieldId);
  const sections = normalized.sections;

  if (target.kind === 'top') {
    const home = sections.find((s) => s.id === moving.sectionId);
    const alone = rest.every((f) => f.sectionId !== moving.sectionId);
    if (home && alone && home.layout === '1') return moveSection(content, home.id, target.index);

    let id = `sec-of-${fieldId}`;
    while (sections.some((s) => s.id === id)) id = `${id}-x`;
    const placed = { ...moving, sectionId: id, column: 0 };
    const nextSections = insertAt(sections, target.index, { id, layout: '1' as SectionLayout });
    return rebuild(content, pruneEmptyLoose(nextSections, [...rest, placed]), [...rest, placed]);
  }

  if (!sections.some((s) => s.id === target.sectionId)) return content;
  const inColumn = rest.filter((f) => f.sectionId === target.sectionId && (f.column ?? 0) === target.column);
  const placed = { ...moving, sectionId: target.sectionId, column: target.column };
  const ordered = insertAt(inColumn, target.index, placed);
  const others = rest.filter((f) => !(f.sectionId === target.sectionId && (f.column ?? 0) === target.column));
  const fields = [...others, ...ordered];
  return rebuild(content, pruneEmptyLoose(sections, fields), fields);
}

export function moveSection(content: HeaderContent, sectionId: string, toIndex: number): HeaderContent {
  const normalized = normalizeHeaderSections(content);
  const moving = normalized.sections.find((s) => s.id === sectionId);
  if (!moving) return content;
  const rest = normalized.sections.filter((s) => s.id !== sectionId);
  return rebuild(content, insertAt(rest, toIndex, moving), normalized.fields);
}

/**
 * One step up/down for an element, crossing containers: past a column's edge it lands in the
 * neighbouring column, then the neighbouring section (a loose element is stepped over as a whole).
 */
export function moveElementBy(content: HeaderContent, fieldId: string, delta: number): HeaderContent {
  const tree = sectionTreeOf(content);
  const si = tree.findIndex((s) => s.columns.some((c) => c.some((f) => f.id === fieldId)));
  const section = tree[si];
  if (!section) return content;
  if (section.implicit) return moveSectionBy(content, section.id, delta);

  const ci = section.columns.findIndex((c) => c.some((f) => f.id === fieldId));
  const list = section.columns[ci] ?? [];
  const from = list.findIndex((f) => f.id === fieldId);
  const to = from + delta;
  if (to >= 0 && to < list.length) return moveElement(content, fieldId, { kind: 'column', sectionId: section.id, column: ci, index: to });

  const intoColumn = (sectionId: string, column: number, index: number) =>
    moveElement(content, fieldId, { kind: 'column', sectionId, column, index });

  if (delta < 0) {
    if (ci > 0) return intoColumn(section.id, ci - 1, section.columns[ci - 1]?.length ?? 0);
    const prev = tree[si - 1];
    if (!prev) return content;
    if (prev.implicit) return moveElement(content, fieldId, { kind: 'top', index: si - 1 });
    const last = prev.columns.length - 1;
    return intoColumn(prev.id, last, prev.columns[last]?.length ?? 0);
  }
  if (ci < section.columns.length - 1) return intoColumn(section.id, ci + 1, 0);
  const next = tree[si + 1];
  if (!next) return content;
  if (next.implicit) return moveElement(content, fieldId, { kind: 'top', index: si + 2 });
  return intoColumn(next.id, 0, 0);
}
export function moveSectionBy(content: HeaderContent, sectionId: string, delta: number): HeaderContent {
  const sections = sectionTreeOf(content);
  const from = sections.findIndex((s) => s.id === sectionId);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= sections.length) return content;
  return moveSection(content, sectionId, to);
}

/** Append a new element to one column (the selected column — never a fresh implicit section). */
export function addElementToColumn(content: HeaderContent, field: BlockField, sectionId: string, column: number): HeaderContent {
  const normalized = normalizeHeaderSections(content);
  if (!normalized.sections.some((s) => s.id === sectionId)) return content;
  return rebuild(content, normalized.sections, [...normalized.fields, { ...field, sectionId, column }]);
}

/** Delete a section only when it holds no element. */
export function removeEmptySection(content: HeaderContent, sectionId: string): HeaderContent {
  const normalized = normalizeHeaderSections(content);
  if (normalized.fields.some((f) => f.sectionId === sectionId)) return content;
  return rebuild(
    content,
    normalized.sections.filter((s) => s.id !== sectionId),
    normalized.fields
  );
}

function mapFields(content: HeaderContent, fn: (fields: BlockField[]) => BlockField[]): HeaderContent {
  const normalized = normalizeHeaderSections(content);
  const fields = fn(normalized.fields);
  return rebuild(content, pruneEmptyLoose(normalized.sections, fields), fields);
}

export function patchElement(content: HeaderContent, fieldId: string, patch: Partial<BlockField>): HeaderContent {
  return mapFields(content, (fields) => fields.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)));
}

export function removeElement(content: HeaderContent, fieldId: string): HeaderContent {
  return mapFields(content, (fields) => fields.filter((f) => f.id !== fieldId));
}

/** Insert a copy straight after its source, in the same column. */
export function duplicateElement(content: HeaderContent, fieldId: string, copyId: string): HeaderContent {
  return mapFields(content, (fields) => {
    const source = fields.find((f) => f.id === fieldId);
    if (!source) return fields;
    const at = fields.findIndex((f) => f.id === fieldId);
    return [...fields.slice(0, at + 1), { ...source, id: copyId }, ...fields.slice(at + 1)];
  });
}
